// Reveal page media only when its pixels are ready. Reinitialised after Swup
// navigation, including images already loaded from the browser cache.
let cleanups = [];

function prepareReveal(media, beforeReveal = () => {}) {
    let frame = 0;
    let queued = false;
    let disposed = false;
    const schedule = () => {
        // Two frames keep the hidden state in the first paint, even if the
        // image was cached or the video started before page initialisation.
        frame = requestAnimationFrame(() => {
            frame = requestAnimationFrame(() => {
                if (!disposed) {
                    beforeReveal();
                    media.classList.add('is-loaded');
                }
            });
        });
    };

    cleanups.push(() => {
        disposed = true;
        cancelAnimationFrame(frame);
        document.removeEventListener('jk:intro-complete', schedule);
    });

    return () => {
        if (disposed || queued) return;
        queued = true;
        // Otherwise a fast/cached image finishes fading behind the intro.
        if (document.documentElement.classList.contains('site-intro-active')) {
            document.addEventListener('jk:intro-complete', schedule, { once: true });
        } else {
            schedule();
        }
    };
}

function revealImage(image) {
    const reveal = prepareReveal(image);
    const loaded = () => {
        if (!image.naturalWidth) return;
        if (image.decode) image.decode().then(reveal, reveal);
        else reveal();
    };
    image.addEventListener('load', loaded);
    cleanups.push(() => image.removeEventListener('load', loaded));
    if (image.complete) loaded();
}

function afterPosterPaint(video, loadVideo) {
    const poster = video.parentElement.querySelector('[data-video-poster]');
    if (!poster) { loadVideo(); return; }
    let disposed = false;
    let started = false;
    let frame = 0;
    const ready = () => {
        if (started || disposed) return;
        started = true;
        // Reuse the picture element's chosen mobile/desktop URL for the native
        // poster too. It remains a fallback until the playback reveal begins.
        if (poster.naturalWidth) video.poster = poster.currentSrc;
        const paint = () => {
            if (disposed) return;
            frame = requestAnimationFrame(() => {
                frame = requestAnimationFrame(() => {
                    if (!disposed) loadVideo();
                });
            });
        };
        if (poster.naturalWidth && poster.decode) poster.decode().then(paint, paint);
        else paint(); // A missing poster must not prevent video playback.
    };
    poster.addEventListener('load', ready, { once: true });
    poster.addEventListener('error', ready, { once: true });
    cleanups.push(() => {
        disposed = true;
        cancelAnimationFrame(frame);
        poster.removeEventListener('load', ready);
        poster.removeEventListener('error', ready);
    });
    if (poster.complete) ready();
}

function revealVideo(video) {
    const reveal = prepareReveal(video, () => {
        // The video fades from black, so its translucent frames must never
        // reveal the static first frame underneath them.
        const poster = video.parentElement.querySelector('[data-video-poster]');
        if (poster) poster.hidden = true;
        video.removeAttribute('poster');
    });
    let videoFrame = null;
    const playing = () => {
        if (video.requestVideoFrameCallback) {
            videoFrame = video.requestVideoFrameCallback(reveal);
        } else {
            reveal();
        }
    };
    // A blocked autoplay attempt must not produce an unhandled rejection or
    // reveal an empty video. A later successful playback can still reveal it.
    const play = () => video.play()?.catch(() => {});
    video.addEventListener('playing', playing, { once: true });
    video.addEventListener('canplay', play, { once: true });
    cleanups.push(() => {
        video.removeEventListener('playing', playing);
        video.removeEventListener('canplay', play);
        if (videoFrame !== null) video.cancelVideoFrameCallback?.(videoFrame);
        video.pause();
    });

    if (!video.querySelector('source') && video.dataset.webmDesktop) {
        afterPosterPaint(video, () => {
            const mobile = window.matchMedia('(max-width: 767px)').matches;
            // The mobile MP4 is smaller and plays in WebKit implementations that
            // advertise WebM support but never reach canplay for the VP9 file.
            const formats = mobile ? ['mp4', 'webm'] : ['webm', 'mp4'];
            for (const format of formats) {
                const src = (mobile && video.dataset[`${format}Mobile`]) || video.dataset[`${format}Desktop`];
                if (!src) continue;
                const source = document.createElement('source');
                source.src = src;
                source.type = `video/${format}`;
                video.appendChild(source);
            }
            video.preload = 'auto';
            video.load();
        });
    } else if (!video.paused && video.readyState >= 2) {
        video.removeEventListener('playing', playing);
        playing();
    } else if (video.readyState >= 3) {
        play();
    }
}

export function init() {
    cleanups.forEach(cleanup => cleanup());
    cleanups = [];
    document.querySelectorAll('img[data-media-reveal]').forEach(revealImage);
    document.querySelectorAll('video[data-media-reveal]').forEach(revealVideo);
}
